import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import MenuItemEditor from "../../components/menuItem/menuItemEditor.comp";
import Layout from "../../components/layouts/layout.comp";
import { createMenuItem, fetchMenuItemList } from "../../core/menuItem.core";
import { useTranslation } from "../../core/translation.core";
import { MenuItemFormData } from "../../types/menuItem";

const emptyLocale = { en: "", zhHant: "", zhHans: "" };

export default function MenuItemsCreatePage() {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const orderDefaultsApplied = useRef(false);

  const { data: menuItemList } = useQuery({
    queryKey: ["menuItemList"],
    queryFn: fetchMenuItemList,
  });

  const methods = useForm<MenuItemFormData>({
    defaultValues: {
      type: "link",
      categoryId: "",
      label: emptyLocale,
      href: emptyLocale,
      order: 0,
    },
  });

  useEffect(() => {
    if (menuItemList === undefined || orderDefaultsApplied.current) return;
    orderDefaultsApplied.current = true;
    const nextOrder =
      menuItemList.length === 0 ? 0 : Math.max(...menuItemList.map((i) => i.order)) + 1;
    methods.reset({
      type: "link",
      categoryId: "",
      label: emptyLocale,
      href: emptyLocale,
      order: nextOrder,
    });
  }, [menuItemList, methods]);

  const mutation = useMutation({
    mutationFn: async (data: MenuItemFormData) => {
      await createMenuItem(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menuItemList"] });
      router.push("/menuItem");
    },
  });

  return (
    <Layout
      title={t(msg.menuItem.create)}
      breadcrumbs={[
        { name: t(msg.menuItem.title), link: "/menuItem" },
        { name: t(msg.menuItem.create), link: "/menuItem/create" },
      ]}
    >
      <MenuItemEditor methods={methods} mutation={mutation} btnText={t(msg.form.create)} />
    </Layout>
  );
}
